import { Head } from "@inertiajs/react"
import { Check, Clock, Users, Zap, Shield, Code, Rocket, TrendingUp } from "lucide-react"
import { CompanyLayout } from "~/components/company/CompanyLayout"
import { ServicePageCTA } from "~/components/company/ServicePageCTA"
import { ContactSection } from "~/components/company/sections/ContactSection"
import { HeroBuildTracker } from "~/components/company/sections/HeroBuildTracker"
import { SectionReveal, StaggerReveal, StaggerItem, FadeScale } from "~/components/company/SectionReveal"

export default function MVPDevelopmentPage() {
  return (
    <CompanyLayout>
      <Head>
        <title>MVP Development in 30 Days | Optimal Dev Labs</title>
        <meta name="description" content="Ship your MVP in 30 days with a complete product team. Strategy, design, full-stack development, and launch included. Fixed scope, fixed timeline, fixed price. Typical projects $15k-$25k." />
        <meta name="keywords" content="mvp development, minimum viable product, ship mvp 30 days, startup product development, product development agency, non-technical founder, full stack development, product team for hire, silicon valley development" />
        <meta property="og:title" content="MVP Development in 30 Days | Optimal Dev Labs" />
        <meta property="og:description" content="Ship your MVP in 30 days with a complete product team. Strategy, design, full-stack development, and launch. Fixed price $15k-$25k." />
        <meta property="og:url" content="https://optimaldevlabs.com/services/mvp-development" />
        <meta name="twitter:title" content="MVP Development in 30 Days | Optimal Dev Labs" />
        <meta name="twitter:description" content="Ship your MVP in 30 days with a complete product team. Fixed price $15k-$25k." />
        <link rel="canonical" href="https://optimaldevlabs.com/services/mvp-development" />
      </Head>
      <main className="flex-1">
        <section className="w-full bg-white py-12 md:py-20 lg:py-28">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center max-w-6xl mx-auto">
              <SectionReveal className="flex flex-col space-y-6 text-center lg:text-left lg:flex-1">
                <div className="space-y-4">
                  <p className="text-[#ed2132] font-semibold text-sm">MVP DEVELOPMENT</p>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight text-balance">
                    Ship Your MVP in 30 Days
                  </h1>
                  <p className="text-lg sm:text-xl md:text-2xl text-slate-600 max-w-xl mx-auto lg:mx-0 text-balance">
                    Stop waiting to hire. We&apos;re the product team that gets your idea to market, fast.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <ServicePageCTA label="Book a Discovery Call" />
                </div>

                <p className="text-sm text-slate-500">
                  Typical projects: $15k-$25k
                </p>
              </SectionReveal>

              <SectionReveal delay={0.2} direction="right" className="w-full lg:flex-1 flex justify-center">
                <HeroBuildTracker />
              </SectionReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <SectionReveal className="max-w-4xl mx-auto">
              <div className="space-y-3 text-center">
                <p className="text-[#ed2132] font-semibold">WHO THIS IS FOR</p>
                <p className="text-lg text-slate-600">
                  Non-technical founders who have a product idea validated and need engineering help to ship. Startups with $15k-$25k budget and a timeline of 30 days or less.
                </p>
              </div>
            </SectionReveal>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  You Know What to Build. You Just Need a Team.
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Most founders get stuck because they can&apos;t find reliable technical help.
                </p>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  {
                    icon: Clock,
                    title: "Hiring Takes Too Long",
                    description: "4-8 weeks to find, vet, and onboard developers. That's $15k-$30k in lost time and opportunity."
                  },
                  {
                    icon: Users,
                    title: "Freelancers Can't Do It All",
                    description: "One person can't handle strategy, design, frontend, backend, and DevOps. Software takes a team."
                  },
                  {
                    icon: TrendingUp,
                    title: "Bad Code Costs More Later",
                    description: "Shortcuts now mean 2-3x rebuilding costs later. You need it built right the first time."
                  },
                  {
                    icon: Zap,
                    title: "Your Time Is Being Wasted",
                    description: "Managing contractors eats 10-20 hours/week. That's time you should spend on sales and users."
                  }
                ].map((problem, idx) => {
                  const Icon = problem.icon
                  return (
                    <StaggerItem key={idx}>
                      <div className="flex gap-4 bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md hover:border-slate-300 transition-all duration-300">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-red-100">
                            <Icon className="h-5 w-5 text-[#ed2132]" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-lg font-semibold text-slate-900">{problem.title}</h3>
                          <p className="text-slate-600 text-sm">{problem.description}</p>
                        </div>
                      </div>
                    </StaggerItem>
                  )
                })}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">THE SOLUTION</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  A Complete Product Team in Your Corner
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  We handle everything from strategy to launch. You focus on your business.
                </p>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: Rocket,
                    title: "Product Strategy",
                    description: "We refine your idea into a buildable MVP. User flows, feature prioritization, technical architecture."
                  },
                  {
                    icon: Code,
                    title: "Design and Development",
                    description: "Full-stack engineering with modern tech. Clean code, scalable architecture, beautiful UI."
                  },
                  {
                    icon: Shield,
                    title: "Launch and Support",
                    description: "Deployment, monitoring, user feedback integration. We stay through launch and early iterations."
                  }
                ].map((solution, idx) => {
                  const Icon = solution.icon
                  return (
                    <StaggerItem key={idx}>
                      <div className="space-y-4 text-center group">
                        <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-red-100 mx-auto transition-transform duration-300 group-hover:scale-110">
                          <Icon className="h-6 w-6 text-[#ed2132]" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">{solution.title}</h3>
                        <p className="text-slate-600 text-sm">{solution.description}</p>
                      </div>
                    </StaggerItem>
                  )
                })}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">WHAT&apos;S INCLUDED</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Everything You Need to Launch
                </h2>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 gap-4" staggerDelay={0.06}>
                {[
                  "Product strategy and roadmap",
                  "UX/UI design (Figma deliverables)",
                  "Full-stack development",
                  "Database design and setup",
                  "Authentication and user management",
                  "Payment integration (if needed)",
                  "Deployment and hosting setup",
                  "Documentation and handoff",
                  "2 weeks post-launch support",
                  "100% code ownership"
                ].map((item, idx) => (
                  <StaggerItem key={idx}>
                    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md hover:border-slate-300 transition-all duration-300">
                      <div className="flex-shrink-0">
                        <Check className="h-5 w-5 text-[#ed2132]" />
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">30-DAY TIMELINE</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  From Kickoff to Live Product
                </h2>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-3 gap-6" staggerDelay={0.15}>
                {[
                  {
                    week: "Week 1",
                    title: "Strategy and Design",
                    tasks: ["Product requirements", "User flows", "Wireframes", "Design system"]
                  },
                  {
                    week: "Weeks 2-3",
                    title: "Build and Iterate",
                    tasks: ["Core features", "Weekly demos", "Feedback integration", "Testing"]
                  },
                  {
                    week: "Week 4",
                    title: "Polish and Launch",
                    tasks: ["Final refinements", "Deployment", "User onboarding", "Go live"]
                  }
                ].map((phase, idx) => (
                  <StaggerItem key={idx}>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-4 hover:shadow-lg hover:border-red-300 transition-all duration-300">
                      <div className="space-y-1">
                        <p className="text-[#ed2132] text-sm font-bold">{phase.week}</p>
                        <h3 className="text-xl font-bold text-slate-900">{phase.title}</h3>
                      </div>
                      <ul className="space-y-2">
                        {phase.tasks.map((task, taskIdx) => (
                          <li key={taskIdx} className="flex items-center gap-2 text-sm text-slate-600">
                            <span className="text-[#ed2132]">•</span>
                            {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <FadeScale className="max-w-3xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-xl p-8 md:p-12 space-y-8 text-center shadow-sm hover:shadow-xl transition-shadow duration-500">
                <div className="space-y-4">
                  <p className="text-[#ed2132] font-semibold">INVESTMENT</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                    Typical Project Cost
                  </h2>
                </div>

                <div className="space-y-2">
                  <p className="text-5xl md:text-6xl font-bold text-[#ed2132]">
                    $15k-$25k
                  </p>
                  <p className="text-slate-600">
                    Fixed scope. Fixed timeline. Fixed price.
                  </p>
                </div>

                <div className="space-y-4 text-left max-w-md mx-auto">
                  <p className="text-sm text-slate-600">
                    <strong>What affects price:</strong>
                  </p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-slate-400">•</span>
                      Complexity of features (auth, payments, integrations)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-slate-400">•</span>
                      Design requirements (custom vs. templates)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-slate-400">•</span>
                      Number of user types and flows
                    </li>
                  </ul>
                </div>

                <div className="pt-4">
                  <ServicePageCTA label="Get a Custom Quote" />
                </div>

                <p className="text-sm text-slate-500">
                  20-minute call. We&apos;ll scope your project and give you an exact quote.
                </p>
              </div>
            </FadeScale>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">WHY WORK WITH US</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Not Your Typical Agency
                </h2>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  {
                    title: "10+ Years in Silicon Valley",
                    description: "We've built and shipped production apps at scale. You get senior expertise, not junior developers learning on your dime."
                  },
                  {
                    title: "Ownership, Not Hours",
                    description: "We don't bill hourly. We commit to outcomes. Your success is our success."
                  },
                  {
                    title: "We Stay After Launch",
                    description: "Most teams disappear after V1. We stay through early user feedback and help you iterate to product-market fit."
                  },
                  {
                    title: "You Own Everything",
                    description: "Full code ownership, documentation, and training. You're never locked in."
                  }
                ].map((reason, idx) => (
                  <StaggerItem key={idx}>
                    <div className="space-y-2 p-4 rounded-lg hover:bg-slate-50 transition-colors duration-300">
                      <h3 className="text-lg font-semibold text-slate-900">{reason.title}</h3>
                      <p className="text-slate-600 text-sm">{reason.description}</p>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <ContactSection
          title="Ready to Ship Your MVP in 30 Days?"
          description="Tell us about your idea. 20-minute call, no pitch. We'll scope your MVP and give you an exact quote."
          inquiryPrompt="Where are you with your product?"
          inquiryOptions={[
            { value: "mvp-idea", label: "I have an idea and need an MVP built" },
            { value: "mvp-specs", label: "I have designs or specs ready to build" },
            { value: "mvp-rebuild", label: "I need to fix or rebuild an existing MVP" },
            { value: "mvp-post-launch", label: "I've launched and need help iterating" },
            { value: "other", label: "Other - please contact me to discuss" },
          ]}
          service="MVP Development"
        />
      </main>
    </CompanyLayout>
  )
}
